import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import logo from '@/assets/logo.png';
import { RoutesLink } from '@/utility/RouteLinks';
import { Printer, HelpCircle, Shield, FileText, RotateCcw, AlertCircle } from 'lucide-react';

interface PublicLayoutProps {
  children: React.ReactNode;
  title: string;
  subtitle?: string;
  lastUpdated?: string;
}

export const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  title,
  subtitle,
  lastUpdated
}) => {
  const location = useLocation();

  const navItems = [
    { label: 'FAQ', path: RoutesLink.faq, icon: HelpCircle },
    { label: 'Terms & Conditions', path: RoutesLink.termsAndConditions, icon: FileText },
    { label: 'Privacy Policy', path: RoutesLink.privacyPolicy, icon: Shield },
    { label: 'Cancellation & Refund', path: RoutesLink.returnPolicy, icon: RotateCcw },
    { label: 'Disclaimer', path: RoutesLink.disclaimer, icon: AlertCircle },
  ];

  return (
    <div className="min-h-screen bg-slate-50 text-foreground flex flex-col font-sans selection:bg-primary/20">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-white/90 backdrop-blur-md border-b border-slate-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16 sm:h-20">
            <Link to="/" className="flex items-center gap-3 group">
              <img src={logo} alt="Yebo Eggs Logo" className="h-10 sm:h-12 w-auto object-contain transition-transform group-hover:scale-105" />
              <div className="flex flex-col">
                <span className="font-extrabold text-lg sm:text-xl tracking-tight text-slate-900 leading-tight">
                  YEBO EGGS
                </span>
                <span className="text-[10px] sm:text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  Legal & Information
                </span>
              </div>
            </Link>

            <button
              onClick={() => window.print()}
              className="hidden sm:inline-flex items-center gap-2 h-9 px-3.5 rounded-xl bg-slate-100 text-slate-700 text-xs font-semibold hover:bg-slate-200 transition-all border border-slate-200 cursor-pointer"
            >
              <Printer className="w-3.5 h-3.5" />
              Print Page
            </button>
          </div>

          {/* Navigation Bar */}
          <nav className="flex items-center gap-1 overflow-x-auto no-scrollbar py-2 -mx-4 px-4 sm:mx-0 sm:px-0 border-t border-slate-100">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path ||
                (item.path === RoutesLink.returnPolicy && location.pathname === RoutesLink.returnPolicyAlias);

              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-2 px-3.5 py-2 rounded-xl text-xs font-semibold whitespace-nowrap transition-all cursor-pointer ${
                    isActive
                      ? 'bg-primary text-white shadow-sm'
                      : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100/80'
                  }`}
                >
                  <Icon className={`w-3.5 h-3.5 ${isActive ? 'text-white' : 'text-slate-400'}`} />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      </header>

      {/* Hero Banner */}
      <div className="bg-gradient-to-b from-white to-slate-100 border-b border-slate-200 py-8 sm:py-12">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center space-y-2">
          <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
            {title}
          </h1>
          {subtitle && (
            <p className="text-sm sm:text-base text-slate-600 max-w-2xl mx-auto leading-relaxed">
              {subtitle}
            </p>
          )}
          {lastUpdated && (
            <p className="text-xs text-slate-400 font-medium pt-1">
              Effective Date / Last Updated: {lastUpdated}
            </p>
          )}
        </div>
      </div>

      {/* Main Content Area */}
      <main className="flex-1 max-w-4xl w-full mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10 space-y-8">
          {children}
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-slate-400 border-t border-slate-800 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs">
          <div className="flex items-center gap-3">
            <img src={logo} alt="Yebo Eggs Logo" className="h-7 w-auto opacity-80" />
            <span className="font-semibold text-slate-300">YEBO EGGS</span>
            <span>&copy; {new Date().getFullYear()} Yebo Eggs. All rights reserved.</span>
          </div>

          <div className="flex items-center gap-4 text-slate-400">
            <Link to={RoutesLink.faq} className="hover:text-slate-200 transition-colors">FAQ</Link>
            <span>&bull;</span>
            <Link to={RoutesLink.termsAndConditions} className="hover:text-slate-200 transition-colors">Terms</Link>
            <span>&bull;</span>
            <Link to={RoutesLink.privacyPolicy} className="hover:text-slate-200 transition-colors">Privacy</Link>
            <span>&bull;</span>
            <Link to={RoutesLink.returnPolicy} className="hover:text-slate-200 transition-colors">Returns & Refunds</Link>
          </div>
        </div>
      </footer>
    </div>
  );
};
